import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SharedNav from '@/components/SharedNav';
import { API } from '@/App';
import { toast } from 'sonner';
import {
  ArrowLeft, Send, Loader2, ShieldCheck, AlertTriangle,
  Database, FileCheck, ExternalLink, RotateCcw,
} from 'lucide-react';

/**
 * /predict/admin/submit-pilot — Auth-gated admin form for submitting
 * validation pilot results. Mirrors the PilotSubmission Pydantic model.
 *
 * On success the pilot is persisted, an event auto-emits to the public
 * validation log, and /predict/validation status escalates per business rules.
 */

const SAGE = '#2D6A4F';
const SAGE_DARK = '#1B4332';
const PAPER = '#FFFFFF';
const SAGE_MUTED = '#6F6A63';

const MODULES = [
  { key: 'historical-yield', label: 'Historical Yield Explorer' },
  { key: 'water-calculator', label: 'Water Requirement Calculator' },
  { key: 'pest-tracker', label: 'Degree-Day Pest Tracker' },
  { key: 'budget', label: 'Crop Budget Calculator' },
];

const STATUSES = [
  { value: 'validated', label: 'Validated — pilot passed target', tone: 'green' },
  { value: 'beta', label: 'BETA — pilot completed but missed target', tone: 'amber' },
  { value: 'pilot-pending', label: 'Pilot pending — partial / preliminary', tone: 'grey' },
];

// Per-module metric relevance (drives conditional form rendering).
const METRIC_FIELDS_BY_MODULE = {
  'historical-yield': ['match_pct'],
  'water-calculator': ['rmse', 'mae'],
  'pest-tracker': ['lead_lag_days'],
  'budget': ['match_pct'],
};

const DEFAULT_FORM = {
  module_key: 'historical-yield',
  crop: '',
  state: '',
  district: '',
  n_observations: '',
  rmse: '',
  mae: '',
  match_pct: '',
  lead_lag_days: '',
  status: 'validated',
  pilot_partner: '',
  pilot_date: new Date().toISOString().slice(0, 10),
  notes: '',
};

export default function AdminSubmitPilot() {
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState(null);
  const [waterCrops, setWaterCrops] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Load catalogues for autocomplete
  useEffect(() => {
    axios.get(`${API}/predict/historical/catalogue`)
      .then(r => setCatalogue(r.data))
      .catch(() => { /* non-fatal — user can type free-text */ });
    axios.get(`${API}/predict/water/crops`)
      .then(r => setWaterCrops(r.data))
      .catch(() => { /* non-fatal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- API + setters are stable
  }, []);

  const cropOptions = useMemo(() => {
    if (form.module_key === 'water-calculator' && waterCrops?.crops) {
      return waterCrops.crops.map(c => ({ key: c.key, label: c.label }));
    }
    if (catalogue?.crops_in_scope_full) {
      return catalogue.crops_in_scope_full;
    }
    return [];
  }, [form.module_key, catalogue, waterCrops]);

  const stateOptions = useMemo(() => {
    return catalogue?.states?.map(s => s.name) || [];
  }, [catalogue]);

  const districtOptions = useMemo(() => {
    if (!catalogue?.districts_per_state || !form.state) return [];
    return catalogue.districts_per_state[form.state] || [];
  }, [catalogue, form.state]);

  const relevantMetrics = METRIC_FIELDS_BY_MODULE[form.module_key] || [];

  const update = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!form.n_observations) {
      setError('n_observations is required.');
      return;
    }
    if (!form.module_key || !form.status) {
      setError('module_key and status are required.');
      return;
    }

    // Build payload — strip empty strings, coerce numerics
    const payload = {
      module_key: form.module_key,
      n_observations: parseInt(form.n_observations, 10),
      status: form.status,
    };
    ['crop', 'state', 'district', 'pilot_partner', 'pilot_date', 'notes'].forEach(k => {
      if (form[k]?.toString().trim()) payload[k] = form[k].toString().trim();
    });
    relevantMetrics.forEach(k => {
      if (form[k] !== '' && form[k] !== null && form[k] !== undefined) {
        payload[k] = parseFloat(form[k]);
      }
    });

    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/predict/validation/submit-pilot`, payload, { withCredentials: true });
      setResult(r.data);
      toast.success('Pilot submitted — log event auto-emitted.');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Submission failed.';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      toast.error('Pilot submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setResult(null);
    setError(null);
  };

  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SharedNav />

      <main className="max-w-[860px] mx-auto px-6 pt-10 pb-24" data-testid="admin-submit-pilot-page">
        <button
          onClick={() => navigate('/predict/validation')}
          className="inline-flex items-center gap-1.5 mb-6 transition-colors"
          style={{ fontSize: 13, color: SAGE_MUTED, background: 'transparent', border: 'none', cursor: 'pointer' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = SAGE; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = SAGE_MUTED; }}
          data-testid="admin-back"
        >
          <ArrowLeft size={14} /> Back to Validation Dashboard
        </button>

        <header className="mb-8" data-testid="admin-header">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span style={{
              background: '#E8EAF6', color: '#2E3580', border: '1px solid #B5BCE0',
              borderRadius: 999, padding: '4px 12px', fontSize: 11.5, fontWeight: 700,
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              <ShieldCheck size={12} className="inline mr-1.5" /> Admin · auth-gated
            </span>
          </div>
          <h1 className="font-display" style={{ fontSize: 38, fontWeight: 400, color: SAGE_DARK, letterSpacing: '-0.01em', lineHeight: 1.1, marginBottom: 6 }}>
            Submit Validation Pilot
          </h1>
          <p className="font-display" style={{ fontSize: 16, color: SAGE_MUTED, fontStyle: 'italic' }}>
            One-step submission · auto-emits a public validation-log event · escalates module status when target is met.
          </p>
        </header>

        {/* Form */}
        {!result && (
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl p-6 mb-6"
            style={{ background: PAPER, border: '1px solid #E5E7DD' }}
            data-testid="admin-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Module" testid="field-module">
                <select value={form.module_key} onChange={update('module_key')} data-testid="input-module" style={inputStyle}>
                  {MODULES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </Field>
              <Field label="Status" testid="field-status">
                <select value={form.status} onChange={update('status')} data-testid="input-status" style={inputStyle}>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>

              <Field label="Crop (optional)" testid="field-crop">
                <select value={form.crop} onChange={update('crop')} data-testid="input-crop" style={inputStyle}>
                  <option value="">— pan-crop —</option>
                  {cropOptions.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="N observations *" testid="field-n-observations">
                <input
                  type="number" min={0} value={form.n_observations} onChange={update('n_observations')}
                  data-testid="input-n-observations" style={inputStyle} required
                  placeholder="50"
                />
              </Field>

              <Field label="State (optional)" testid="field-state">
                <select value={form.state} onChange={update('state')} data-testid="input-state" style={inputStyle}>
                  <option value="">— pan-India —</option>
                  {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="District (optional)" testid="field-district">
                <select
                  value={form.district} onChange={update('district')} data-testid="input-district"
                  style={{ ...inputStyle, opacity: districtOptions.length === 0 ? 0.55 : 1 }}
                  disabled={districtOptions.length === 0}
                >
                  <option value="">— state-level —</option>
                  {districtOptions.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
            </div>

            {/* Metric fields — only render the metrics relevant to the chosen module */}
            <div className="mt-5">
              <div
                className="font-body mb-3"
                style={{ fontSize: 11, color: SAGE, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}
              >
                Module-specific metrics ({form.module_key})
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relevantMetrics.includes('rmse') && (
                  <Field label="RMSE" testid="field-rmse">
                    <input
                      type="number" step="0.01" value={form.rmse} onChange={update('rmse')}
                      data-testid="input-rmse" style={inputStyle} placeholder="0.42"
                    />
                  </Field>
                )}
                {relevantMetrics.includes('mae') && (
                  <Field label="MAE" testid="field-mae">
                    <input
                      type="number" step="0.01" value={form.mae} onChange={update('mae')}
                      data-testid="input-mae" style={inputStyle} placeholder="0.31"
                    />
                  </Field>
                )}
                {relevantMetrics.includes('match_pct') && (
                  <Field label="Match %" testid="field-match-pct">
                    <input
                      type="number" min={0} max={100} step="0.1" value={form.match_pct} onChange={update('match_pct')}
                      data-testid="input-match-pct" style={inputStyle} placeholder="100.0"
                    />
                  </Field>
                )}
                {relevantMetrics.includes('lead_lag_days') && (
                  <Field label="|Lead/lag| days" testid="field-lead-lag">
                    <input
                      type="number" step="0.1" value={form.lead_lag_days} onChange={update('lead_lag_days')}
                      data-testid="input-lead-lag-days" style={inputStyle} placeholder="3.5"
                    />
                  </Field>
                )}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Pilot partner" testid="field-pilot-partner">
                <input
                  type="text" value={form.pilot_partner} onChange={update('pilot_partner')}
                  data-testid="input-pilot-partner" style={inputStyle}
                  placeholder="Dr. Praveen Kumar · AU Jodhpur"
                />
              </Field>
              <Field label="Pilot date" testid="field-pilot-date">
                <input
                  type="date" value={form.pilot_date} onChange={update('pilot_date')}
                  data-testid="input-pilot-date" style={inputStyle}
                />
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Notes" testid="field-notes">
                <textarea
                  rows={3} value={form.notes} onChange={update('notes')}
                  data-testid="input-notes" style={{ ...inputStyle, resize: 'vertical', minHeight: 78 }}
                  placeholder="What was validated? Source dataset reference. Any caveats."
                />
              </Field>
            </div>

            {error && (
              <div
                className="mt-5 rounded-xl p-3 flex items-start gap-2"
                style={{ background: '#FBE9E3', border: '1px solid #C87A5E', color: '#8A2B1A' }}
                data-testid="admin-error"
              >
                <AlertTriangle size={15} style={{ marginTop: 2, flexShrink: 0 }} />
                <span className="font-body" style={{ fontSize: 13 }}>{error}</span>
              </div>
            )}

            <div className="mt-6 flex items-center gap-3 justify-end">
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center gap-1.5"
                style={secondaryButtonStyle}
                data-testid="admin-reset"
              >
                <RotateCcw size={13} /> Reset
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2"
                style={{ ...primaryButtonStyle, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                data-testid="admin-submit"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {submitting ? 'Submitting…' : 'Submit pilot'}
              </button>
            </div>
          </form>
        )}

        {/* Success result */}
        {result && (
          <div className="rounded-2xl p-6 mb-6" style={{ background: PAPER, border: '1px solid #C8DBC0' }} data-testid="admin-result">
            <div className="flex items-center gap-2 mb-4">
              <FileCheck size={18} style={{ color: SAGE }} />
              <span className="font-body" style={{ fontSize: 13, color: SAGE, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Pilot submitted & log event emitted
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ResultPanel title="Stored pilot record" testid="result-pilot">
                <pre style={preStyle}>{JSON.stringify(result.pilot, null, 2)}</pre>
              </ResultPanel>
              <ResultPanel title="Auto-emitted log event" testid="result-event">
                <pre style={preStyle}>{JSON.stringify(result.log_event, null, 2)}</pre>
              </ResultPanel>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                onClick={() => navigate('/predict/validation')}
                style={primaryButtonStyle}
                data-testid="admin-result-go-validation"
              >
                <Database size={14} className="inline mr-1.5" />
                See on Validation Dashboard
              </button>
              <button
                onClick={() => navigate('/predict/validation-log')}
                style={secondaryButtonStyle}
                data-testid="admin-result-go-log"
              >
                <ExternalLink size={13} className="inline mr-1.5" />
                See on Validation Log
              </button>
              <button
                onClick={resetForm}
                style={{ ...secondaryButtonStyle, marginLeft: 'auto' }}
                data-testid="admin-result-submit-another"
              >
                <Send size={13} className="inline mr-1.5" />
                Submit another
              </button>
            </div>
          </div>
        )}

        <p className="font-body" style={{ fontSize: 12, color: SAGE_MUTED, fontStyle: 'italic', textAlign: 'center' }}>
          Submissions are auth-gated and append-only. Once a log event lands it is publicly visible at /predict/validation-log.
        </p>
      </main>
    </div>
  );
}

const inputStyle = {
  fontSize: 13.5,
  color: SAGE_DARK,
  border: '1px solid #D8DBD0',
  background: '#FAFBF9',
  borderRadius: 8,
  padding: '8px 11px',
  width: '100%',
  fontFamily: 'inherit',
};

const primaryButtonStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: '#FFFFFF',
  background: SAGE,
  border: '1px solid ' + SAGE_DARK,
  padding: '9px 16px',
  borderRadius: 8,
  cursor: 'pointer',
};

const secondaryButtonStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: SAGE_DARK,
  background: PAPER,
  border: '1px solid ' + SAGE_DARK,
  padding: '9px 16px',
  borderRadius: 8,
  cursor: 'pointer',
};

const preStyle = {
  fontSize: 11,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  background: '#FAFBF9',
  border: '1px solid #E5E7DD',
  borderRadius: 6,
  padding: 10,
  overflow: 'auto',
  maxHeight: 280,
  color: SAGE_DARK,
};

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="font-body block mb-1.5" style={{ fontSize: 11, color: SAGE_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function ResultPanel({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="font-body mb-2" style={{ fontSize: 11, color: SAGE, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
        {title}
      </div>
      {children}
    </div>
  );
}
